import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { Card, KpiCard } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/Badge';
import { CountryFlag } from '../components/ui/CountryFlag';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatRelativeTime,
  formatDateTime,
  getDeviceLabel,
} from '../lib/utils';
import {
  DollarSign,
  MousePointer,
  Target,
  TrendingUp,
  Smartphone,
  Monitor,
  Tablet,
  Bot,
  HelpCircle,
} from 'lucide-react';
import { useAuth } from '../store/auth';
import { usePublisherLookup } from '../hooks/useLookups';

// ─── Helpers ────────────────────────────────────────────────────────────────

function DeviceIcon({ device, className = '' }: { device: string | null | undefined; className?: string }) {
  const d = (device ?? '').toLowerCase();
  const iconClass = `inline-block ${className}`;
  if (d.includes('bot')) return <Bot size={12} className={`${iconClass} text-neon-orange`} />;
  if (d.includes('mobile')) return <Smartphone size={12} className={`${iconClass} text-neon-cyan`} />;
  if (d.includes('tablet')) return <Tablet size={12} className={`${iconClass} text-neon-purple`} />;
  if (d.includes('desktop')) return <Monitor size={12} className={`${iconClass} text-neon-green`} />;
  return <HelpCircle size={12} className={`${iconClass} text-text-tertiary`} />;
}

const RANGES = ['today', '7d', '30d'] as const;
type Range = (typeof RANGES)[number];

function rangeToOverviewParam(r: Range): string {
  return r;
}
function rangeDays(r: Range): number {
  return r === 'today' ? 1 : r === '7d' ? 7 : 30;
}

interface OverviewStats {
  clicksTotal: number;
  clicksUnique: number;
  clicksQuality: number;
  clicksBot: number;
  conversionsApproved: number;
  conversionsPending: number;
  revenueGross: number;
  payoutGross: number;
  cr: number;
  epc: number;
}

interface LiveClick {
  click_id: string;
  sub_id: string | null;
  campaign_slug: string | null;
  offer_id: number | null;
  country: string | null;
  device: string | null;
  os: string | null;
  source: string | null;
  ip: string | null;
  is_bot: number;
  ts: number;
  conversion: { revenue: number; payout: number; status: string } | null;
}

interface Conversion {
  id: number;
  clickId: string;
  subId: string | null;
  country: string | null;
  deviceType: string | null;
  revenue: number;
  payout: number;
  status: string;
  eventType: string;
  createdAt: number;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function MyEarningsPage() {
  const { user } = useAuth();
  const [range, setRange] = useState<Range>('today');
  // BUGFIX (audit Perf #3): MyEarnings previously hardcoded 5s/10s/30s
  // polling without `pollMult`. Publishers (role=user, pollMult=2) hit AE
  // 2x faster than admins on the SAME data — primary cause of the AE 429
  // rate-limit errors users reported. Also bumped clicks cadence 5s → 15s
  // since publisher views don't need sub-10s freshness on a personal page.
  const pollMult = usePollMultiplier();

  // Aggregate stats (AE clicks + raw conversions, scoped to my publisher via mine=true)
  const overviewQ = useQuery({
    queryKey: ['my-earnings', 'overview', range],
    queryFn: () =>
      apiClient.get<OverviewStats>('/api/v1/stats/overview', {
        range: rangeToOverviewParam(range),
        mine: 'true',
      }),
    refetchInterval: range === 'today' ? 30_000 * pollMult : false,
    refetchIntervalInBackground: false,
  });

  // Live clicks (Analytics Engine, scoped to my sub_id)
  const clicksQ = useQuery({
    queryKey: ['my-earnings', 'clicks', range],
    queryFn: () =>
      apiClient.get<LiveClick[]>('/api/v1/clicks/live', {
        since_days: rangeDays(range),
        mine: 'true',
        limit: 200,
      }),
    refetchInterval: range === 'today' ? 15_000 * pollMult : false,
    refetchIntervalInBackground: false,
  });

  // Conversions (D1, scoped to my publisher)
  const conversionsQ = useQuery({
    queryKey: ['my-earnings', 'conversions', range],
    queryFn: () =>
      apiClient.get<Conversion[]>('/api/v1/conversions', {
        since_days: rangeDays(range),
        mine: 'true',
        limit: 200,
      }),
    refetchInterval: range === 'today' ? 15_000 * pollMult : false,
    refetchIntervalInBackground: false,
  });

  const stats = overviewQ.data;
  const clicks = clicksQ.data ?? [];
  const conversions = conversionsQ.data ?? [];

  // Quick KPIs derived from live clicks (used as fallback while D1 aggregator catches up)
  const liveAgg = useMemo(() => {
    const totalClicks = clicks.length;
    const botClicks = clicks.filter((c) => c.is_bot === 1).length;
    const qualityClicks = totalClicks - botClicks;
    const convs = conversions.filter((c) => c.status === 'approved' || c.status === 'paid');
    const revenue = convs.reduce((s, c) => s + (c.revenue ?? 0), 0);
    const cr = qualityClicks > 0 ? (convs.length / qualityClicks) * 100 : 0;
    const epc = qualityClicks > 0 ? revenue / qualityClicks : 0;
    return {
      totalClicks,
      qualityClicks,
      botClicks,
      conversions: convs.length,
      revenue,
      cr,
      epc,
    };
  }, [clicks, conversions]);

  // Prefer D1 aggregator for older windows (more accurate / pre-aggregated)
  // For "today" range, use live values which update in real time
  const displayClicks = range === 'today' ? liveAgg.qualityClicks : stats?.clicksQuality ?? liveAgg.qualityClicks;
  const displayConv = range === 'today' ? liveAgg.conversions : stats?.conversionsApproved ?? liveAgg.conversions;
  const displayRevenue = range === 'today' ? liveAgg.revenue : stats?.revenueGross ?? liveAgg.revenue;
  const displayCR = range === 'today' ? liveAgg.cr : stats?.cr ?? liveAgg.cr;
  const displayEPC = range === 'today' ? liveAgg.epc : stats?.epc ?? liveAgg.epc;

  // Resolve sub_id from the publisher lookup first (authoritative — works
  // even when publisher has zero clicks today). Falls back to a row-derived
  // value only if lookup hasn't loaded yet. Previously this picked the
  // first click/conversion row's sub_id, so a brand-new publisher with no
  // traffic saw "—" instead of their actual sub_id.
  const publisherLookup = usePublisherLookup();
  const subId = user?.publisherId
    ? publisherLookup.getName(user.publisherId)
        ?? clicks.find((c) => c.sub_id)?.sub_id
        ?? conversions.find((c) => c.subId)?.subId
        ?? '—'
    : '—';

  // Orphan-user banner: a `user`-role account with publisherId=null can't
  // see any data (backend filters to publisher_id = -1 sentinel). Without
  // a hint, the publisher sees all-zeros and wonders why. Show a clear
  // banner explaining they need to be linked.
  const isOrphan = user?.role === 'user' && user.publisherId == null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Orphan-user banner — account exists but isn't linked to a publisher
          row, so backend filters return zero rows. Without this hint user
          sees all-zeros silently and assumes the dashboard is broken. */}
      {isOrphan && (
        <div className="flex items-start gap-3 px-4 py-3 border border-neon-yellow/40 bg-neon-yellow/5 rounded-sm">
          <span className="text-neon-yellow text-lg leading-none">⚠</span>
          <div className="text-xs font-mono text-neon-yellow leading-relaxed">
            <p className="font-bold mb-1">Akun belum di-link ke publisher.</p>
            <p className="text-neon-yellow/80">
              Hubungi admin agar akun kamu ditautkan ke sub_id publisher. Sampai itu
              dilakukan, halaman ini tidak akan menampilkan data.
            </p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <DollarSign size={20} className="text-neon-green" />
            MY EARNINGS
          </h1>
          <p className="text-sm text-text-secondary font-mono">
            // performance pribadi · sub_id={' '}
            <code className="text-neon-cyan">{subId}</code>
          </p>
        </div>

        {/* Range selector */}
        <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm transition-all ${
                range === r ? 'bg-neon-green/10 text-neon-green' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Clicks"
          value={formatNumber(displayClicks)}
          icon={<MousePointer size={16} />}
          accent="cyan"
          delta={
            range === 'today'
              ? { value: `${liveAgg.botClicks} bot · ${liveAgg.totalClicks} total`, positive: false }
              : undefined
          }
        />
        <KpiCard
          label="Conversions"
          value={formatNumber(displayConv)}
          icon={<Target size={16} />}
          accent="magenta"
        />
        <KpiCard
          label="Revenue"
          value={formatCurrency(displayRevenue)}
          icon={<DollarSign size={16} />}
          accent="yellow"
        />
        <KpiCard
          label="CR"
          value={formatPercent(displayCR, 2)}
          icon={<TrendingUp size={16} />}
          accent="green"
        />
        <KpiCard
          label="EPC"
          value={formatCurrency(displayEPC)}
          icon={<DollarSign size={16} />}
          accent="purple"
        />
      </div>

      {/* Click Stream */}
      <Card className="p-0">
        <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
          <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
            <MousePointer size={14} />
            My Click Stream
            <span className="text-[10px] font-mono text-text-tertiary normal-case tracking-normal">
              ({clicks.length})
            </span>
            {range === 'today' && (
              <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] text-neon-green font-mono">
                <span className="pulse-dot" />
                LIVE · {15 * pollMult}s
              </span>
            )}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Time</th>
                <th>Campaign</th>
                <th>Country</th>
                <th>Device</th>
                <th>Source</th>
                <th className="text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {clicks.map((c) => (
                <tr key={c.click_id} className={c.is_bot === 1 ? 'opacity-50' : ''}>
                  <td
                    className="font-mono text-xs text-text-secondary whitespace-nowrap"
                    title={formatDateTime(c.ts)}
                  >
                    {formatRelativeTime(c.ts)}
                  </td>
                  <td className="font-mono text-xs">{c.campaign_slug ?? '—'}</td>
                  <td>
                    {c.country ? <CountryFlag iso2={c.country} showCode size={20} /> : '—'}
                  </td>
                  <td>
                    {c.device ? (
                      <span className="flex items-center gap-1.5 font-mono text-xs">
                        <DeviceIcon device={c.device} />
                        <span>{getDeviceLabel(c.device)}</span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="font-mono text-xs text-text-secondary">{c.source ?? '—'}</td>
                  <td className="text-right">
                    {c.conversion ? (
                      <span
                        className={`inline-flex items-center font-mono text-[10px] px-1.5 py-0.5 rounded border ${
                          c.conversion.status === 'reversed' || c.conversion.status === 'rejected'
                            ? 'border-neon-red/40 text-neon-red bg-neon-red/5 line-through'
                            : 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5'
                        }`}
                      >
                        {formatCurrency(c.conversion.revenue)}
                      </span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {clicks.length === 0 && !clicksQ.isLoading && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-tertiary">
                    <MousePointer className="mx-auto mb-2 opacity-30" size={28} />
                    <p className="font-mono text-xs">no clicks yet untuk window ini</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Conversion Stream */}
      <Card className="p-0">
        <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
          <h3 className="font-display text-sm uppercase tracking-wider text-neon-magenta flex items-center gap-2">
            <Target size={14} />
            My Conversions
            <span className="text-[10px] font-mono text-text-tertiary normal-case tracking-normal">
              ({conversions.length})
            </span>
            {range === 'today' && (
              <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] text-neon-green font-mono">
                <span className="pulse-dot" />
                LIVE · {15 * pollMult}s
              </span>
            )}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Time</th>
                <th>Click ID</th>
                <th>Country</th>
                <th>Event</th>
                <th className="text-right">Revenue</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {conversions.map((c) => (
                <tr
                  key={c.id}
                  className={c.status === 'reversed' || c.status === 'rejected' ? 'opacity-50' : ''}
                >
                  <td
                    className="font-mono text-xs text-text-secondary whitespace-nowrap"
                    title={formatDateTime(c.createdAt)}
                  >
                    {formatRelativeTime(c.createdAt)}
                  </td>
                  <td>
                    <code className="text-[10px] font-mono text-text-secondary bg-bg-tertiary px-1.5 py-0.5 rounded">
                      {c.clickId?.slice(0, 8)}…
                    </code>
                  </td>
                  <td>{c.country ? <CountryFlag iso2={c.country} showCode size={20} /> : '—'}</td>
                  <td>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-text-secondary bg-bg-tertiary px-2 py-0.5 rounded">
                      {c.eventType}
                    </span>
                  </td>
                  <td className="text-right numeric text-neon-yellow">{formatCurrency(c.revenue)}</td>
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
              {conversions.length === 0 && !conversionsQ.isLoading && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-tertiary">
                    <Target className="mx-auto mb-2 opacity-30" size={28} />
                    <p className="font-mono text-xs">no conversions yet untuk window ini</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
